const TxDirectionEnum = {
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw",
} as const;

const RiskScoreLevelEnum = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  UNKNOWN: "unknown",
} as const;

const AmlStatusEnum = {
  NEW: "new",
  ERROR: "error",
  FAILED: "failed",
  PENDING: "pending",
  SUCCESS: "success",
} as const;

const BlackConnectionEnum = {
  OUT: "BlackListOut",
  IN: "BlackListIn",
  OUT_INTERACTION: "BlackListOutInteraction",
} as const;

const AmlFlowEnum = {
  ACCURATE: "accurate",
  ADVANCED: "advanced",
  FAST: "fast",
  LIGHT: "light",
  LITE: "lite",
  FAST_LIMITED: "fast_limited",
  ADVANCED_LIMITED: "advanced_limited",
  INSTANT: "instant",
} as const;

const ChainsEnum = {
  BTC: "BTC",
  LTC: "LTC",
  BCH: "BCH",
  BSV: "BSV",
  DOGE: "DOGE",
  TETHER_OMNI: "TetherOMNI",

  ETH: "ETH",
  ETC: "ETC",
  BSC: "BSC",
  POLYGON: "POLYGON",
  // backwards compatibility
  MATIC: "MATIC",
  AVAX: "AVAX",
  ARB: "ARB",
  OP: "OP",
  BASE: "BASE",

  TRX: "TRX",
  ALGO: "ALGO",
  ADA: "ADA",
  ZEC: "ZEC",
  SOL: "SOL",
  DOT: "DOT",

  XRP: "XRP",
  XLM: "XLM",
  TON: "TON",
  NEAR: "NEAR",
  XTZ: "XTZ",
  APT: "APT",
} as const;

type Chain = {
  symbol: string;
  networkCode: string;
  name: string;
  order: number;
  isEvm: boolean;
  skipAddressCoins: boolean;
  addressRegex?: RegExp;
  txnRegex?: RegExp;
  precision: number;
  viewPrecision: number;
};

const ChainsDictionary: Record<string, Chain> = {
  [ChainsEnum.BTC]: {
    symbol: ChainsEnum.BTC,
    networkCode: ChainsEnum.BTC,
    name: "Bitcoin",
    order: 0,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(?:bc1:)?[a-z0-9]{30,60}$/i,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 8,
    viewPrecision: 6,
  },
  [ChainsEnum.LTC]: {
    symbol: ChainsEnum.LTC,
    networkCode: ChainsEnum.LTC,
    name: "Litecoin",
    order: 3,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(?:ltc1:)?[a-z0-9]{30,60}$/i,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 8,
    viewPrecision: 6,
  },
  [ChainsEnum.BCH]: {
    symbol: ChainsEnum.BCH,
    networkCode: ChainsEnum.BCH,
    name: "Bitcoin Cash",
    order: 4,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(?:bitcoincash:)?[a-z0-9]{30,60}$/i,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 8,
    viewPrecision: 6,
  },
  [ChainsEnum.BSV]: {
    symbol: ChainsEnum.BSV,
    networkCode: ChainsEnum.BSV,
    name: "Bitcoin SV",
    order: 8,
    isEvm: false,
    skipAddressCoins: false,
    precision: 8,
    viewPrecision: 6,
  },
  [ChainsEnum.DOGE]: {
    symbol: ChainsEnum.DOGE,
    networkCode: ChainsEnum.DOGE,
    name: "Dogecoin",
    order: 9,
    isEvm: false,
    skipAddressCoins: false,
    precision: 8,
    viewPrecision: 8,
  },
  [ChainsEnum.TETHER_OMNI]: {
    symbol: ChainsEnum.TETHER_OMNI,
    networkCode: ChainsEnum.TETHER_OMNI,
    name: ChainsEnum.TETHER_OMNI,
    order: 6,
    isEvm: false,
    skipAddressCoins: false,
    precision: 8,
    viewPrecision: 6,
  },
  [ChainsEnum.ETH]: {
    symbol: ChainsEnum.ETH,
    networkCode: ChainsEnum.ETH,
    name: "Ethereum",
    order: 1,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 9,
    viewPrecision: 6,
  },
  [ChainsEnum.ETC]: {
    symbol: ChainsEnum.ETC,
    networkCode: ChainsEnum.ETC,
    name: "Ethereum Classic",
    order: 7,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 9,
    viewPrecision: 6,
  },
  [ChainsEnum.BSC]: {
    symbol: "BNB",
    networkCode: ChainsEnum.BSC,
    name: "BNB Chain",
    order: 2,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 9,
    viewPrecision: 6,
  },
  [ChainsEnum.MATIC]: {
    symbol: ChainsEnum.MATIC,
    networkCode: ChainsEnum.MATIC,
    name: "Polygon",
    order: 11,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 18,
    viewPrecision: 4,
  },
  [ChainsEnum.AVAX]: {
    symbol: ChainsEnum.AVAX,
    networkCode: ChainsEnum.AVAX,
    name: "Avalanche",
    order: 18,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 18,
    viewPrecision: 4,
  },
  [ChainsEnum.ARB]: {
    symbol: "ETH",
    networkCode: ChainsEnum.ARB,
    name: "Arbitrum",
    order: 16,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 0,
    viewPrecision: 9,
  },
  [ChainsEnum.TRX]: {
    symbol: ChainsEnum.TRX,
    networkCode: ChainsEnum.TRX,
    name: "TRON",
    order: 10,
    isEvm: false,
    skipAddressCoins: true,
    precision: 6,
    viewPrecision: 6,
  },
  [ChainsEnum.ALGO]: {
    symbol: ChainsEnum.ALGO,
    networkCode: ChainsEnum.ALGO,
    name: "Algorand",
    order: 15,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^[A-Z2-7]{58}$/,
    txnRegex: /^[A-Z2-7]{52}$/,
    precision: 6,
    viewPrecision: 6,
  },
  [ChainsEnum.ADA]: {
    symbol: ChainsEnum.ADA,
    networkCode: ChainsEnum.ADA,
    name: "Cardano",
    order: 12,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(?:addr1|A|D)[a-zA-Z0-9]{30,130}$/,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 6,
    viewPrecision: 6,
  },
  [ChainsEnum.ZEC]: {
    symbol: ChainsEnum.ZEC,
    networkCode: ChainsEnum.ZEC,
    name: "Zcash",
    order: 13,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^t[0-9][a-z0-9]{33}$/i,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 8,
    viewPrecision: 8,
  },
  [ChainsEnum.SOL]: {
    symbol: ChainsEnum.SOL,
    networkCode: ChainsEnum.SOL,
    name: "Solana",
    order: 14,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^[0-9a-z]{40,64}$/i,
    txnRegex: /^[a-z0-9]{64,88}$/i,
    precision: 9,
    viewPrecision: 6,
  },
  [ChainsEnum.DOT]: {
    symbol: ChainsEnum.DOT,
    networkCode: ChainsEnum.DOT,
    name: "Polkadot",
    order: 17,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^1[0-9a-z]{47}$/,
    txnRegex: /^[0-9]+-[0-9]+$/,
    precision: 10,
    viewPrecision: 10,
  },
  [ChainsEnum.XRP]: {
    symbol: ChainsEnum.XRP,
    networkCode: ChainsEnum.XRP,
    name: "Ripple",
    order: 5,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^r[0-9a-z]{24,34}$/i,
    txnRegex: /^[a-f0-9]{64}$/i,
    precision: 6,
    viewPrecision: 6,
  },
  [ChainsEnum.XLM]: {
    symbol: ChainsEnum.XLM,
    networkCode: ChainsEnum.XLM,
    name: "Stellar",
    order: 19,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^G[A-Z0-9]{55}$/,
    txnRegex: /^[a-f0-9]{64}$/,
    precision: 7,
    viewPrecision: 7,
  },
  [ChainsEnum.TON]: {
    symbol: ChainsEnum.TON,
    networkCode: ChainsEnum.TON,
    name: "TON",
    order: 20,
    isEvm: false,
    skipAddressCoins: false,
    precision: 0,
    viewPrecision: 9,
  },
  [ChainsEnum.NEAR]: {
    symbol: ChainsEnum.NEAR,
    networkCode: ChainsEnum.NEAR,
    name: "Near",
    order: 21,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(([a-z0-9]+[-_])*[a-z0-9]+\.)*([a-z0-9]+[-_])*[a-z0-9]+$/,
    txnRegex: /^[A-z0-9]{43,44}$/,
    precision: 24,
    viewPrecision: 5,
  },
  [ChainsEnum.XTZ]: {
    symbol: ChainsEnum.XTZ,
    networkCode: ChainsEnum.XTZ,
    name: "Tezos",
    order: 22,
    isEvm: false,
    skipAddressCoins: false,
    addressRegex: /^(tz[1-4]{1})|(KT1)|(txr1)|(sr1)$/,
    txnRegex: /^o+.*$/,
    precision: 6,
    viewPrecision: 6,
  },
  [ChainsEnum.OP]: {
    symbol: "ETH",
    networkCode: ChainsEnum.OP,
    name: "Optimism",
    order: 23,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 18,
    viewPrecision: 6,
  },
  [ChainsEnum.BASE]: {
    symbol: "ETH",
    networkCode: ChainsEnum.BASE,
    name: "Base",
    order: 16,
    isEvm: true,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{40}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 18,
    viewPrecision: 6,
  },
  [ChainsEnum.APT]: {
    symbol: "APT",
    networkCode: ChainsEnum.APT,
    name: "Aptos",
    order: 24,
    isEvm: false,
    skipAddressCoins: true,
    addressRegex: /^0x[0-9a-f]{64}$/i,
    txnRegex: /^0x[0-9a-f]{64}$/i,
    precision: 8,
    viewPrecision: 8,
  },
};

const AssetPrecisionEnum = {
  TOKEN_VIEW_PRECISION_DEFAULT: 2,
  PRECISION_DEFAULT: 8,
  VIEW_PRECISION_DEFAULT: 6,
} as const;

export {
  ChainsEnum,
  ChainsDictionary,
  AssetPrecisionEnum,
  AmlStatusEnum,
  TxDirectionEnum,
  RiskScoreLevelEnum,
  AmlFlowEnum,
  BlackConnectionEnum,
};

export type RiskScoreLevelEnum =
  (typeof RiskScoreLevelEnum)[keyof typeof RiskScoreLevelEnum];
export type AmlStatusEnum = (typeof AmlStatusEnum)[keyof typeof AmlStatusEnum];
export type AmlFlowEnum = (typeof AmlFlowEnum)[keyof typeof AmlFlowEnum];
export type BlackConnectionEnum =
  (typeof BlackConnectionEnum)[keyof typeof BlackConnectionEnum];
export type ChainsEnum = (typeof ChainsEnum)[keyof typeof ChainsEnum];
export type { Chain };
export type AssetPrecisionEnum =
  (typeof AssetPrecisionEnum)[keyof typeof AssetPrecisionEnum];
export type TxDirectionEnum =
  (typeof TxDirectionEnum)[keyof typeof TxDirectionEnum];
